import type { ReactNode } from 'react';
import { FiList } from 'react-icons/fi';
import { styles } from '@/shared/app-stepper-form/styles';

/**
 * The control that opens the step sheet on a phone.
 *
 * WHY IT SHOWS "Step 1/3" RATHER THAN AN ICON
 * -------------------------------------------
 * An icon-only trigger is a control the user has to LEARN. A list glyph beside a
 * heading could mean the step list, a table of contents, or nothing at all, and
 * the only way to find out is to tap it — which is a poor trade on the one
 * control that tells the user how much of the form is left.
 *
 * The count is the single most useful fact about a multi-step form, and it is
 * the fact the phone layout otherwise hides: the rail that carries it on a wide
 * screen is `hidden` below `lg`. Printing it on the trigger puts it back in the
 * layout for the cost of one short line, and the icon beside it says what the
 * tap will do. The label is the affordance; the icon is the hint.
 *
 * WHY IT IS STILL A BUTTON WITH A NAME
 * ------------------------------------
 * The visible text is "Step 1/3", which is a position, not an action. The
 * accessible name spells out both — "Show all steps — step 1 of 3" — so a screen
 * reader hears what the control does as well as where the user is.
 *
 * THE NAME IS "Show all steps", NOT THE STEP'S NAME. The rail row for the active
 * step is also a button, and it carries that step's name. Repeating the name here
 * would put two buttons called "Legal entity" in the accessibility tree with no
 * way to tell them apart.
 */
export function StepSheetTrigger({
  index,
  total,
  open,
  onOpen,
}: {
  index: number;
  total: number;
  open: boolean;
  onOpen: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={styles.stepSheetTrigger}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`Show all steps — step ${index + 1} of ${total}`}
    >
      <span aria-hidden="true">
        Step {index + 1}/{total}
      </span>
      <FiList aria-hidden="true" className="size-4" />
    </button>
  );
}
